import {css, view} from '@sail/ui/deprecated';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {defineMessages} from 'react-intl';
import {useWidgetTitle} from '@stripe-internal/conversations-ui';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {ConfigContext} from '../Providers/ConfigProvider';
import {
  ConnectPlatformSharingNotice,
  useConnectPlatformTransparency,
} from '../components/ConnectPlatformTransparency';
import {
  ChannelSelector,
  useEligibleChannels,
} from '../components/ChannelSelector';
import {TopicSelector, Topic} from '../components/TopicSelector';
import {Title} from '../components/Title';
import {
  MessageToParentType,
  ParentMessageBusContext,
} from '../Providers/ParentMessageBusProvider';
import {ScreenName, useScreen} from '../Providers/ScreenProvider';
import {useClientAnalytics} from '../Providers/ClientProvider/useClientAnalytics';
import {ChannelType} from '../components/ChannelSelector/Channel';

export const messages = defineMessages({
  chooseChannel: {
    id: 'support_conversations_client.splashScreen.chooseChannel',
    description:
      'Asks a user how they would like to contact support on the splash screen',
    defaultMessage: 'How would you like to connect with a support agent?',
  },
});

export function SplashScreen() {
  const {setTitle} = useWidgetTitle();
  const {transitionTo} = useScreen();
  const analytics = useClientAnalytics();
  const {post} = useContext(ParentMessageBusContext);
  const {entrypoint, urls, disableTopicSelector} = useContext(ConfigContext);
  const {eligibleChannels, noticeMessage} = useEligibleChannels();
  const {platformName, showSharingNotice} = useConnectPlatformTransparency();

  const [selectedTopic, setSelectedTopic] = useState<Topic | undefined>(
    undefined,
  );

  const requestOpenEmailModal = useCallback(() => {
    post({
      type: MessageToParentType.RequestOpenEmailModal,
      data: {
        topic: selectedTopic,
      },
    });
  }, [post, selectedTopic]);

  useEffect(() => {
    setTitle(<Title />);
  }, [setTitle]);

  const emailContactUrl = useMemo(() => {
    const emailTopicParam = selectedTopic ? `?topic=${selectedTopic}` : '';
    return `${urls.supportSite}/contact/email${emailTopicParam}`;
  }, [urls, selectedTopic]);

  const styles = css({
    display: css.value('flex'),
    flexDirection: css.value('column'),
  });

  return (
    <view.div
      data-testid="SplashScreen"
      uses={[styles]}
      css={disableTopicSelector ? {paddingTop: 'medium'} : undefined}
    >
      {!disableTopicSelector ? (
        <TopicSelector
          selectedTopic={selectedTopic}
          onSelectTopic={setSelectedTopic}
        />
      ) : undefined}
      <ChannelSelector
        eligibleChannels={eligibleChannels}
        noticeMessage={noticeMessage}
        selectedTopic={selectedTopic}
        onChannelSelected={(channel: ChannelType) => {
          analytics.action('channel_selected', {
            channel: channel.type,
            topic: selectedTopic,
            entrypoint,
          });
          switch (channel.type) {
            case 'chat':
              transitionTo({
                name: ScreenName.Chat,
                props: {context: {topic: selectedTopic}},
              });
              break;
            case 'request_a_call':
              transitionTo({
                name: ScreenName.RequestACall,
                props: {
                  topic: selectedTopic!,
                  waitTime: channel.waitEstimate,
                },
              });
              break;
            case 'email':
              if (entrypoint === 'dashboard') {
                requestOpenEmailModal();
              } else {
                safeWindowOpen(emailContactUrl, '_blank');
              }
              break;
            default:
          }
        }}
      />
      {showSharingNotice && (
        <ConnectPlatformSharingNotice platformNickname={platformName} />
      )}
    </view.div>
  );
}
